import React, { useState, useEffect, useCallback } from 'react';
import { transactionsAPI } from './api';

const fmt = (n, cur = 'INR') =>
  (cur === 'INR' ? '₹ ' : cur + ' ') +
  parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const CAT_ICONS = {
  salary: '👤', rent: '🏢', utilities: '💡', travel: '✈️',
  supplies: '📦', tax: '🧾', software: '💻',
  invoice_payment: '💰', advance: '📥', other_income: '📈', other: '📋',
};

const DEBIT_CATS  = ['salary','rent','utilities','travel','supplies','tax','software','other'];
const CREDIT_CATS = ['invoice_payment','advance','other_income','other'];
const PAGE_SIZE   = 20;

/* ── Add Entry Modal ─────────────────────────── */
function AddEntryModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    type: 'debit', amount: '', description: '',
    category: 'other', tx_date: new Date().toISOString().split('T')[0], currency: 'INR',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const cats = form.type === 'debit' ? DEBIT_CATS : CREDIT_CATS;

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try { await transactionsAPI.add(form); onSaved(); onClose(); }
    catch (er) { setErr(er.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box">
        <div className="modal-header">
          <h3 className="modal-title">Add Ledger Entry</h3>
          <div className="modal-type-toggle">
            <button type="button" className={form.type === 'debit'  ? 'active' : ''} onClick={() => { set('type','debit');  set('category','other'); }}>Debit</button>
            <button type="button" className={form.type === 'credit' ? 'active' : ''} onClick={() => { set('type','credit'); set('category','other'); }}>Credit</button>
          </div>
        </div>
        <form onSubmit={submit} className="modal-body">
          <div className="modal-grid2">
            <div className="modal-fg">
              <label className="modal-lbl">Amount</label>
              <input className="modal-inp" type="number" required step="0.01" min="0.01"
                value={form.amount} onChange={e => set('amount', e.target.value)} placeholder="0.00" />
            </div>
            <div className="modal-fg">
              <label className="modal-lbl">Currency</label>
              <select className="modal-inp" value={form.currency} onChange={e => set('currency', e.target.value)}>
                {['INR','USD','EUR','GBP','AED','SGD'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="modal-fg">
              <label className="modal-lbl">Date</label>
              <input className="modal-inp" type="date" value={form.tx_date} onChange={e => set('tx_date', e.target.value)} />
            </div>
            <div className="modal-fg">
              <label className="modal-lbl">Category</label>
              <select className="modal-inp" value={form.category} onChange={e => set('category', e.target.value)}>
                {cats.map(c => <option key={c} value={c}>{c.replace(/_/g,' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="modal-fg">
            <label className="modal-lbl">Description *</label>
            <input className="modal-inp" required value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="e.g. Office rent June 2026" />
          </div>
          {err && <div className="modal-error">{err}</div>}
          <div className="modal-footer">
            <button type="button" className="modal-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-save" disabled={saving}>
              {saving ? 'Saving…' : `Add ${form.type === 'debit' ? 'Debit' : 'Credit'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Main Ledger Page ────────────────────────── */
export default function Ledger() {
  const [transactions, setTransactions] = useState([]);
  const [balance,      setBalance]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [showModal,    setShowModal]    = useState(false);

  /* Filters */
  const [typeFilter, setTypeFilter] = useState('all');
  const [catFilter,  setCatFilter]  = useState('all');
  const [search,     setSearch]     = useState('');
  const [page,       setPage]       = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (typeFilter !== 'all') params.type = typeFilter;
      const [txs, bal] = await Promise.all([transactionsAPI.list(params), transactionsAPI.balance()]);
      setTransactions(txs);
      setBalance(bal);
    } catch {}
    finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { load(); setPage(1); }, [load]);

  /* All categories from data */
  const allCats = [...new Set(transactions.map(t => t.category).filter(Boolean))].sort();

  /* Apply filters + search */
  const filtered = transactions.filter(t => {
    if (catFilter !== 'all' && t.category !== catFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.description || '').toLowerCase().includes(q) ||
           (t.invoice_number || '').toLowerCase().includes(q) ||
           (t.category || '').includes(q);
  });

  const totalPages   = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage     = Math.min(page, totalPages);
  const paginated    = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const totalCredit  = filtered.filter(t => t.type === 'credit').reduce((s, t) => s + parseFloat(t.amount), 0);
  const totalDebit   = filtered.filter(t => t.type === 'debit').reduce((s, t) => s + parseFloat(t.amount), 0);

  const handleDelete = async (id, cat) => {
    if (cat === 'invoice_payment') {
      alert('Invoice payment entries are auto-managed. Change the invoice payment status to remove them.');
      return;
    }
    if (!window.confirm('Delete this entry?')) return;
    try { await transactionsAPI.remove(id); load(); } catch (err) { alert(err.message); }
  };

  return (
    <div className="ledger-page">
      {/* Header */}
      <div className="ledger-topbar">
        <div>
          <h1 className="ledger-title">Bank Ledger</h1>
          <p className="ledger-sub">All debit and credit transactions</p>
        </div>
        <button className="ledger-add-btn" onClick={() => setShowModal(true)}>+ Add Entry</button>
      </div>

      {/* Balance summary */}
      {balance && (
        <div className="ledger-summary">
          <div className="ls-card ls-opening">
            <div className="ls-label">Opening Balance</div>
            <div className="ls-value">{fmt(balance.opening)}</div>
          </div>
          <div className="ls-card ls-credit">
            <div className="ls-label">Total Credits</div>
            <div className="ls-value">{fmt(balance.total_credits)}</div>
          </div>
          <div className="ls-card ls-debit">
            <div className="ls-label">Total Debits</div>
            <div className="ls-value">{fmt(balance.total_debits)}</div>
          </div>
          <div className={`ls-card ${parseFloat(balance.current_balance) >= 0 ? 'ls-balance-pos' : 'ls-balance-neg'}`}>
            <div className="ls-label">Current Balance</div>
            <div className="ls-value">{fmt(balance.current_balance)}</div>
          </div>
          {typeFilter !== 'all' || catFilter !== 'all' || search ? (
            <div className="ls-card ls-filtered">
              <div className="ls-label">Filtered: Credits</div>
              <div className="ls-value">{fmt(totalCredit)}</div>
            </div>
          ) : null}
          {typeFilter !== 'all' || catFilter !== 'all' || search ? (
            <div className="ls-card ls-filtered-d">
              <div className="ls-label">Filtered: Debits</div>
              <div className="ls-value">{fmt(totalDebit)}</div>
            </div>
          ) : null}
        </div>
      )}

      {/* Filters */}
      <div className="ledger-filters">
        <div className="ledger-type-tabs">
          {['all','credit','debit'].map(t => (
            <button key={t} className={`ledger-type-tab${typeFilter===t?' active':''}`}
              onClick={() => { setTypeFilter(t); setPage(1); }}>
              {t === 'all' ? 'All Entries' : t === 'credit' ? '+ Credits' : '− Debits'}
            </button>
          ))}
        </div>
        <div className="ledger-filter-row">
          <select className="ledger-select" value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}>
            <option value="all">All Categories</option>
            {allCats.map(c => <option key={c} value={c}>{CAT_ICONS[c] || '📋'} {c.replace(/_/g,' ')}</option>)}
          </select>
          <div className="ledger-search-wrap">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input className="ledger-search" value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search description, category…" />
          </div>
          <span className="ledger-count">{filtered.length} entr{filtered.length !== 1 ? 'ies' : 'y'}</span>
        </div>
      </div>

      {/* Table */}
      <div className="ledger-table-wrap">
        {loading ? (
          <div className="ledger-loading">Loading transactions…</div>
        ) : paginated.length === 0 ? (
          <div className="ledger-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.2">
              <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
            </svg>
            <p>{transactions.length === 0 ? 'No transactions yet. Add a debit or credit entry.' : 'No entries match the current filter.'}</p>
          </div>
        ) : (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Category</th>
                <th>Description</th>
                <th>Reference</th>
                <th className="th-right">Amount</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(tx => (
                <tr key={tx.id}>
                  <td className="td-date">{fmtDate(tx.tx_date)}</td>
                  <td>
                    <span className={`ledger-type-badge ${tx.type}`}>
                      {tx.type === 'credit' ? '+ Credit' : '− Debit'}
                    </span>
                  </td>
                  <td className="td-cat">
                    <span className="cat-icon">{CAT_ICONS[tx.category] || '📋'}</span>
                    {(tx.category || 'other').replace(/_/g,' ')}
                  </td>
                  <td className="td-desc">{tx.description || '—'}</td>
                  <td className="td-ref">
                    {tx.invoice_number
                      ? <span className="ledger-ref">#{tx.invoice_number}</span>
                      : tx.reference || '—'
                    }
                  </td>
                  <td className={`td-amount ${tx.type}`}>
                    {tx.type === 'credit' ? '+' : '−'} {fmt(tx.amount, tx.currency)}
                  </td>
                  <td className="td-del">
                    {tx.category !== 'invoice_payment' ? (
                      <button className="ledger-del-btn" onClick={() => handleDelete(tx.id, tx.category)} title="Delete">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
                          <path d="M9 6V4h6v2"/>
                        </svg>
                      </button>
                    ) : (
                      <span className="ledger-auto-badge" title="Auto-managed via invoice payment">auto</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="ledger-pagination">
          <button className="pg-btn" disabled={safePage === 1} onClick={() => setPage(1)}>«</button>
          <button className="pg-btn" disabled={safePage === 1} onClick={() => setPage(p => p - 1)}>‹</button>
          <div className="pg-info">
            Page <strong>{safePage}</strong> of <strong>{totalPages}</strong>
            <span className="pg-sub">({filtered.length} total)</span>
          </div>
          <button className="pg-btn" disabled={safePage === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
          <button className="pg-btn" disabled={safePage === totalPages} onClick={() => setPage(totalPages)}>»</button>
        </div>
      )}

      {showModal && <AddEntryModal onClose={() => setShowModal(false)} onSaved={load} />}
    </div>
  );
}
